import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Shield, Server, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface PortResult {
  port: number;
  status: 'open' | 'closed' | 'filtered' | 'scanning';
  service?: string;
  description?: string;
  responseTime?: number;
}

interface ScanStats {
  total: number;
  open: number;
  closed: number;
  filtered: number;
  scanned: number;
}

const commonPorts = [
  { port: 21, service: 'FTP', description: 'File Transfer Protocol' },
  { port: 22, service: 'SSH', description: 'Secure Shell' },
  { port: 23, service: 'Telnet', description: 'Telnet Protocol' },
  { port: 25, service: 'SMTP', description: 'Simple Mail Transfer Protocol' },
  { port: 53, service: 'DNS', description: 'Domain Name System' },
  { port: 80, service: 'HTTP', description: 'Hypertext Transfer Protocol' },
  { port: 110, service: 'POP3', description: 'Post Office Protocol v3' },
  { port: 143, service: 'IMAP', description: 'Internet Message Access Protocol' },
  { port: 443, service: 'HTTPS', description: 'HTTP Secure' },
  { port: 993, service: 'IMAPS', description: 'IMAP over SSL' },
  { port: 995, service: 'POP3S', description: 'POP3 over SSL' },
  { port: 3389, service: 'RDP', description: 'Remote Desktop Protocol' },
  { port: 5432, service: 'PostgreSQL', description: 'PostgreSQL Database' },
  { port: 3306, service: 'MySQL', description: 'MySQL Database' },
  { port: 1433, service: 'MSSQL', description: 'Microsoft SQL Server' },
  { port: 6379, service: 'Redis', description: 'Redis Database' },
  { port: 27017, service: 'MongoDB', description: 'MongoDB Database' },
  { port: 8080, service: 'HTTP-Alt', description: 'HTTP Alternative Port' },
];

// This will be moved inside the component to access t function

export const PortScannerTool: React.FC = () => {
  const { t } = useTranslation();

  const portPresets = [
    { name: t('tools.portScanner.presets.commonPorts'), ports: '21,22,23,25,53,80,110,143,443,993,995' },
    { name: t('tools.portScanner.presets.webServers'), ports: '80,443,8080,8443,8000,3000,5000' },
    { name: t('tools.portScanner.presets.databases'), ports: '3306,5432,1433,27017,6379,5984' },
    { name: t('tools.portScanner.presets.mailServers'), ports: '25,110,143,465,587,993,995' },
    { name: t('tools.portScanner.presets.remoteAccess'), ports: '22,23,3389,5900,5901' },
    { name: t('tools.portScanner.presets.wellKnown'), ports: '1-1024' },
  ];

  const [host, setHost] = useState('');
  const [portRange, setPortRange] = useState('80,443,22,21,25');
  const [isScanning, setIsScanning] = useState(false);
  const [results, setResults] = useState<PortResult[]>([]);
  const [stats, setStats] = useState<ScanStats | null>(null);
  const [error, setError] = useState('');
  const [scanSpeed, setScanSpeed] = useState<'fast' | 'normal' | 'slow'>('normal');
  const scanAbortRef = useRef<boolean>(false);

  const getServiceInfo = (port: number) => {
    return commonPorts.find(p => p.port === port);
  };

  const parsePortRange = (range: string): number[] => {
    const ports: number[] = [];
    const parts = range.split(',');
    
    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.includes('-')) {
        const [start, end] = trimmed.split('-').map(p => parseInt(p.trim()));
        if (start && end && start <= end && start > 0 && end <= 65535) {
          for (let i = start; i <= Math.min(end, start + 1000); i++) { // Limit range to prevent browser freeze
            ports.push(i);
          }
        }
      } else {
        const port = parseInt(trimmed);
        if (port && port > 0 && port <= 65535) {
          ports.push(port);
        }
      }
    }
    
    return [...new Set(ports)].sort((a, b) => a - b);
  };

  // Mock port scan for demonstration (browser limitations prevent real port scanning)
  const mockPortScan = async (hostname: string, port: number): Promise<PortResult> => {
    const delay = scanSpeed === 'fast' ? 100 : scanSpeed === 'normal' ? 300 : 500;
    await new Promise(resolve => setTimeout(resolve, delay + Math.random() * 200));
    
    if (scanAbortRef.current) {
      throw new Error('Scan aborted');
    }
    
    const serviceInfo = getServiceInfo(port);
    
    // Simulate realistic port scan results
    let status: 'open' | 'closed' | 'filtered';
    const random = Math.random();
    
    // Common ports are more likely to be open
    const isCommonPort = commonPorts.some(p => p.port === port);
    if (isCommonPort) {
      status = random < 0.3 ? 'open' : random < 0.8 ? 'closed' : 'filtered';
    } else {
      status = random < 0.05 ? 'open' : random < 0.9 ? 'closed' : 'filtered';
    }
    
    return {
      port,
      status,
      service: serviceInfo?.service,
      description: serviceInfo?.description,
      responseTime: Math.round(10 + Math.random() * 100)
    };
  };

  const validateHost = (hostname: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/;
    return ipRegex.test(hostname) || domainRegex.test(hostname) || hostname === 'localhost';
  };

  const startScan = async () => {
    if (!host.trim()) {
      setError(t('tools.portScanner.enterHostError'));
      return;
    }

    if (!validateHost(host.trim())) {
      setError(t('tools.portScanner.invalidHostError'));
      return;
    }

    const ports = parsePortRange(portRange);
    if (ports.length === 0) {
      setError(t('tools.portScanner.invalidPortsError'));
      return;
    }

    if (ports.length > 100) {
      setError(t('tools.portScanner.tooManyPortsError'));
      return;
    }

    setIsScanning(true);
    setError('');
    setResults([]);
    scanAbortRef.current = false;

    const initialResults: PortResult[] = ports.map(port => ({
      port,
      status: 'scanning' as const
    }));
    setResults(initialResults);

    const initialStats: ScanStats = {
      total: ports.length,
      open: 0,
      closed: 0,
      filtered: 0,
      scanned: 0
    };
    setStats(initialStats);

    try {
      for (let i = 0; i < ports.length; i++) {
        if (scanAbortRef.current) break;

        const port = ports[i];
        try {
          const result = await mockPortScan(host.trim(), port);
          
          setResults(prev => prev.map(r => 
            r.port === port ? result : r
          ));

          setStats(prev => {
            if (!prev) return null;
            const newStats = { ...prev };
            newStats.scanned++;
            if (result.status === 'open') newStats.open++;
            else if (result.status === 'closed') newStats.closed++;
            else if (result.status === 'filtered') newStats.filtered++;
            return newStats;
          });
        } catch (err) {
          if (!scanAbortRef.current) {
            setResults(prev => prev.map(r => 
              r.port === port ? { ...r, status: 'closed' as const } : r
            ));
          }
        }
      }
    } catch (err) {
      setError(t('tools.portScanner.scanError'));
    } finally {
      setIsScanning(false);
    }
  };

  const stopScan = () => {
    scanAbortRef.current = true;
    setIsScanning(false);
  };

  const clearResults = () => {
    setResults([]);
    setStats(null);
    setError('');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'closed': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'filtered': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'scanning': return <Clock className="w-4 h-4 text-blue-600 animate-spin" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-green-600 dark:text-green-400';
      case 'closed': return 'text-red-600 dark:text-red-400';
      case 'filtered': return 'text-yellow-600 dark:text-yellow-400';
      case 'scanning': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Scanner Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>{t('tools.portScanner.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label={t('tools.portScanner.targetHost')}
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder={t('tools.portScanner.hostPlaceholder')}
                  disabled={isScanning}
                />
              </div>
              <div>
                <Input
                  label={t('tools.portScanner.portsLabel')}
                  value={portRange}
                  onChange={(e) => setPortRange(e.target.value)}
                  placeholder={t('tools.portScanner.portsPlaceholder')}
                  disabled={isScanning}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.portScanner.scanSpeed')}
                </label>
                <select
                  value={scanSpeed}
                  onChange={(e) => setScanSpeed(e.target.value as 'fast' | 'normal' | 'slow')}
                  disabled={isScanning}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="fast">{t('tools.portScanner.speedOptions.fast')}</option>
                  <option value="normal">{t('tools.portScanner.speedOptions.normal')}</option>
                  <option value="slow">{t('tools.portScanner.speedOptions.slow')}</option>
                </select>
              </div>
              <div className="flex items-end">
                <div className="flex gap-2 w-full">
                  <Button
                    onClick={isScanning ? stopScan : startScan}
                    disabled={!host.trim() || !portRange.trim()}
                    variant="primary"
                    className="flex-1"
                  >
                    {isScanning ? (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        {t('tools.portScanner.stopScan')}
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        {t('tools.portScanner.startScan')}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={clearResults}
                    disabled={isScanning}
                    variant="outline"
                  >
                    {t('common.clear')}
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Port Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.portScanner.quickPresets')}:
              </label>
              <div className="flex flex-wrap gap-2">
                {portPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    onClick={() => setPortRange(preset.ports)}
                    disabled={isScanning}
                    variant="outline"
                    size="sm"
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scan Statistics */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.portScanner.scanProgress')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.scanned}/{stats.total}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.portScanner.scanned')}</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats.open}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.portScanner.open')}</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {stats.closed}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.portScanner.closed')}</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {stats.filtered}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.portScanner.filtered')}</div>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.total > 0 ? Math.round((stats.scanned / stats.total) * 100) : 0}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.portScanner.complete')}</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.total > 0 ? (stats.scanned / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scan Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Server className="w-5 h-5" />
              <span>{t('tools.portScanner.scanResultsFor')} {host}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {results
                .filter(result => result.status === 'open')
                .concat(results.filter(result => result.status !== 'open'))
                .map((result, index) => (
                <div
                  key={result.port}
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(result.status)}
                    <span className="font-mono text-sm font-medium">
                      {t('tools.portScanner.port')} {result.port}
                    </span>
                    {result.service && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded text-xs">
                        {result.service}
                      </span>
                    )}
                    {result.description && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {result.description}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`text-sm font-medium ${getStatusColor(result.status)}`}>
                      {result.status.toUpperCase()}
                    </span>
                    {result.responseTime && (
                      <span className="text-xs text-gray-500">
                        {result.responseTime}ms
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Notice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <span>{t('tools.portScanner.securityNotice')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p>
              <strong>{t('tools.portScanner.educationalPurpose')}:</strong> {t('tools.portScanner.educationalDescription')}
            </p>
            <p>
              <strong>{t('tools.portScanner.browserLimitations')}:</strong> {t('tools.portScanner.limitationsDescription')}
            </p>
            <p>
              <strong>{t('tools.portScanner.legalNotice')}:</strong> {t('tools.portScanner.legalDescription')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
